import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { createClient } from "@supabase/supabase-js";
import { SleepReminderCard } from "./SleepReminderCard";
import { MiniSleepUpdateDialog } from "./MiniSleepUpdateDialog";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

interface SleepRemindersProps {
  childId: string;
  childName?: string;
}

export function SleepReminders({ childId, childName }: SleepRemindersProps) {
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const { data: incompleteSessions = [] } = useQuery({
    queryKey: ['incompleteSessions', childId],
    queryFn: async () => {
      const { data, error } = await supabaseClient
        .from('sleep_sessions')
        .select('*')
        .eq('child_id', childId)
        .eq('status', 'in_progress')
        .order('start_time', { ascending: false })
        .limit(5);

      if (error) throw error;
      return data || [];
    },
    enabled: !!childId,
    refetchInterval: 60000,
  });

  const { data: restfulnessRatings = [] } = useQuery({
    queryKey: ['restfulnessRatings'],
    queryFn: async () => {
      const { data, error } = await supabaseClient
        .from('restfulness')
        .select('id, rating')
        .order('rating');
      
      if (error) throw error;
      return data.map(r => ({ id: r.id, description: r.rating }));
    },
  });

  const handleUpdate = (session: any) => {
    setSelectedSession(session);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSession(null);
  };

  if (incompleteSessions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {incompleteSessions.map((session) => (
        <SleepReminderCard
          key={session.id}
          session={session}
          onUpdate={handleUpdate}
          childName={childName}
        />
      ))}

      {selectedSession && (
        <MiniSleepUpdateDialog
          open={dialogOpen}
          onOpenChange={handleCloseDialog}
          session={selectedSession}
          childName={childName}
          restfulnessRatings={restfulnessRatings}
        />
      )}
    </div>
  );
}
